// src/components/digital_twin/SimulationResultsView.js
// --- UPDATE: v1.0 — results view for every simulation scenario
//   KPIs per scenario family · damage-state bar · restoration curve + crews ·
//   ensemble P10/P50/P90 · weather used · storm playback · top at-risk assets
//   (click to fly to) · warnings · JSON / CSV export.
import React, { memo, useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert, AlertDescription, AlertIcon, Badge, Box, Button, ButtonGroup, Collapse, Flex, HStack,
  Icon, IconButton, SimpleGrid, Slider, SliderFilledTrack, SliderThumb, SliderTrack, Text, Tooltip,
  VStack, useColorModeValue,
} from '@chakra-ui/react';
import {
  FaBrain, FaClock, FaDollarSign, FaFileExport, FaPause, FaPlay, FaUsers, FaWind, FaWater,
  FaExclamationTriangle, FaCrosshairs, FaRoute,
} from 'react-icons/fa';
import { isStormScenario, CATEGORY_LABELS, isRunoffScenario } from './simulationScenarios';
import { FaCloudRain, FaArrowDown } from 'react-icons/fa'; // --- UPDATE (elevation)

const DAMAGE_COLORS = { none: '#48BB78', slight: '#ECC94B', moderate: '#ED8936', extensive: '#E53E3E', complete: '#742A2A' };
const STATE_SCHEME = { critical: 'red', warning: 'orange', ok: 'green' };

const fmtUsd = (v) => (v == null ? '—' : v >= 1e6 ? `$${(v / 1e6).toFixed(1)}M` : v >= 1e3 ? `$${(v / 1e3).toFixed(0)}k` : `$${Math.round(v)}`);
const fmtHour = (h) => (h == null ? '—' : `T${h >= 0 ? '+' : ''}${h}h`);

const csvCell = (v) => {
  if (v === null || v === undefined) return '';
  const s = typeof v === 'object' ? JSON.stringify(v) : String(v);
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
};

const download = (content, name, type) => {
  const blob = new Blob([content], { type });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = name; a.click();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
};

const Kpi = memo(({ label, value, sub, color, icon }) => {
  const bg = useColorModeValue('gray.50', 'whiteAlpha.50');
  const border = useColorModeValue('gray.200', 'whiteAlpha.200');
  const muted = useColorModeValue('gray.500', 'gray.400');
  return (
    <Box bg={bg} borderWidth="1px" borderColor={border} borderRadius="lg" p={2.5}>
      <HStack spacing={1.5} mb={0.5}>
        {icon && <Icon as={icon} boxSize={2.5} color={muted} />}
        <Text fontSize="9px" fontWeight="800" color={muted} textTransform="uppercase" letterSpacing="0.06em" noOfLines={1}>{label}</Text>
      </HStack>
      <Text fontSize="lg" fontWeight="900" lineHeight="1.1" color={color} fontFamily="mono">{value}</Text>
      {sub && <Text fontSize="9px" color={muted} noOfLines={1}>{sub}</Text>}
    </Box>
  );
});
Kpi.displayName = 'SimKpi';

const DamageBar = memo(({ states }) => {
  const total = Object.values(states || {}).reduce((a, b) => a + b, 0) || 1;
  const muted = useColorModeValue('gray.500', 'gray.400');
  return (
    <Box>
      <Flex h="10px" borderRadius="full" overflow="hidden" bg="gray.200" role="img" aria-label="Damage state distribution">
        {Object.entries(DAMAGE_COLORS).map(([k, c]) => (states?.[k] ? (
          <Tooltip key={k} label={`${k}: ${states[k]}`} hasArrow>
            <Box w={`${(states[k] / total) * 100}%`} bg={c} />
          </Tooltip>
        ) : null))}
      </Flex>
      <HStack spacing={3} mt={1} flexWrap="wrap">
        {Object.entries(DAMAGE_COLORS).map(([k, c]) => (
          <HStack key={k} spacing={1}>
            <Box w="7px" h="7px" borderRadius="full" bg={c} />
            <Text fontSize="9px" color={muted} textTransform="capitalize">{k} <b>{states?.[k] ?? 0}</b></Text>
          </HStack>
        ))}
      </HStack>
    </Box>
  );
});
DamageBar.displayName = 'DamageBar';

const RestorationCurve = memo(({ curve }) => {
  const stroke = useColorModeValue('#3182CE', '#63B3ED');
  const grid = useColorModeValue('#E2E8F0', 'rgba(255,255,255,0.1)');
  if (!curve || curve.length < 2) return null;
  const W = 300, H = 70, maxDay = curve[curve.length - 1].day || 1;
  const pts = curve.map(p => `${(p.day / maxDay) * W},${H - (p.network_in_service_pct / 100) * H}`).join(' ');
  return (
    <svg viewBox={`0 0 ${W} ${H}`} width="100%" height={H} preserveAspectRatio="none" role="img" aria-label="Network in service over time">
      {[25, 50, 75].map(y => <line key={y} x1="0" x2={W} y1={H - (y / 100) * H} y2={H - (y / 100) * H} stroke={grid} strokeWidth="1" />)}
      <polyline points={`0,${H} ${pts} ${W},${H}`} fill={stroke} fillOpacity="0.12" stroke="none" />
      <polyline points={pts} fill="none" stroke={stroke} strokeWidth="2" vectorEffect="non-scaling-stroke" />
    </svg>
  );
});
RestorationCurve.displayName = 'RestorationCurve';

const Playback = memo(({ sim }) => {
  const { hourRange, playbackHour, setPlaybackHour, timelineFrame } = sim;
  const [playing, setPlaying] = useState(false);
  const muted = useColorModeValue('gray.500', 'gray.400');

  useEffect(() => {
    if (!playing || !hourRange) return undefined;
    const id = setInterval(() => {
      setPlaybackHour((h) => {
        const next = (h == null ? hourRange.min : h + hourRange.step);
        if (next > hourRange.max) { setPlaying(false); return hourRange.max; }
        return next;
      });
    }, 350);
    return () => clearInterval(id);
  }, [playing, hourRange, setPlaybackHour]);

  if (!hourRange) return null;
  const value = playbackHour ?? hourRange.max;
  return (
    <Box>
      <Flex justify="space-between" align="center" mb={1}>
        <HStack spacing={1.5}>
          <Icon as={FaClock} boxSize={3} color="purple.400" />
          <Text fontSize="10px" fontWeight="800" textTransform="uppercase" letterSpacing="0.06em">Storm playback</Text>
        </HStack>
        <HStack spacing={1}>
          <Text fontSize="10px" fontFamily="mono" color="purple.400">{playbackHour == null ? 'PEAK / FINAL' : fmtHour(value)}</Text>
          <IconButton size="xs" aria-label={playing ? 'Pause playback' : 'Play storm'} icon={<Icon as={playing ? FaPause : FaPlay} boxSize={2.5} />}
            onClick={() => { if (!playing && (playbackHour == null || playbackHour >= hourRange.max)) setPlaybackHour(hourRange.min); setPlaying(p => !p); }} />
          {playbackHour != null && <Button size="xs" variant="ghost" onClick={() => { setPlaying(false); setPlaybackHour(null); }}>Reset</Button>}
        </HStack>
      </Flex>
      <Slider aria-label="Storm hour" min={hourRange.min} max={hourRange.max} step={hourRange.step} value={value}
        onChange={(v) => { setPlaying(false); setPlaybackHour(v); }} colorScheme="purple">
        <SliderTrack><SliderFilledTrack /></SliderTrack>
        <SliderThumb boxSize={3} />
      </Slider>
      {timelineFrame && (
        <HStack spacing={3} fontSize="9px" color={muted} fontFamily="mono">
          <Text>wind {timelineFrame.max_wind_kts} kt</Text>
          <Text>rain {timelineFrame.mean_rain_rate_mm_hr} mm/h</Text>
          <Text>flooded {timelineFrame.assets_flooded}</Text>
          <Text>≥64 kt {timelineFrame.assets_hurricane_wind}</Text>
        </HStack>
      )}
    </Box>
  );
});
Playback.displayName = 'Playback';

const AtRiskRow = memo(({ r, onFocus, storm }) => {
  const hover = useColorModeValue('gray.50', 'whiteAlpha.100');
  const muted = useColorModeValue('gray.500', 'gray.400');
  const pct = Math.round((r.impact_score ?? 0) * 100);
  return (
    <Flex as="button" type="button" w="100%" textAlign="left" align="center" gap={2} px={2} py={1.5}
      borderRadius="md" _hover={{ bg: hover }} onClick={() => onFocus?.(r)} title="Fly to asset">
      <Box w="6px" h="26px" borderRadius="full" bg={storm ? DAMAGE_COLORS[r.damage_state] || 'gray.300' : `${STATE_SCHEME[r.state] || 'gray'}.400`} flexShrink={0} />
      <Box flex={1} minW={0}>
        <Text fontSize="11px" fontWeight="700" noOfLines={1}>{r.name || `${r.asset_type || 'Asset'} #${r.asset_id}`}</Text>
        <Text fontSize="9px" color={muted} noOfLines={1}>
          {r.asset_type || r.layer}
          {storm && r.damage_state && r.damage_state !== 'none' ? ` · ${r.damage_state}` : ''}
          {storm && r.peak_flood_depth_m > 0 ? ` · ${r.peak_flood_depth_m} m flood` : ''}
          {!r.evaluated && r.cascade_from_asset_id ? ` · via #${r.cascade_from_asset_id}` : ''}
          {r.simulated_psi != null ? ` · ${r.simulated_psi} psi` : ''}
          {/* --- UPDATE (elevation): run-off detail */}
          {r.utilisation_pct != null ? ` · ${r.utilisation_pct}% of capacity` : ''}
          {r.layer === 'drainage' && r.peak_flood_depth_m > 0 ? ` · ${r.peak_flood_depth_m} m ponding` : ''}
          {r.clearance_pct != null && r.clearance_pct < 60 ? ` · silt ${r.clearance_pct}%` : ''}
        </Text>
      </Box>
      <Badge colorScheme={STATE_SCHEME[r.state] || 'gray'} fontSize="9px">{pct}%</Badge>
      <Icon as={FaCrosshairs} boxSize={2.5} color={muted} />
    </Flex>
  );
});
AtRiskRow.displayName = 'AtRiskRow';

const SimulationResultsView = ({ sim, onFocusAsset, onClear }) => {
  const { result, meta } = sim;
  const [showWarnings, setShowWarnings] = useState(false);
  const [listLimit, setListLimit] = useState(12);
  const cardBg = useColorModeValue('white', 'gray.800');
  const border = useColorModeValue('gray.200', 'gray.700');
  const muted = useColorModeValue('gray.500', 'gray.400');

  const scenario = meta?.scenario;
  const storm = isStormScenario(scenario);
  const s = result?.summary || {};
  const restoration = result?.restoration;
  const ensemble = result?.ensemble;
  const weather = result?.weather;
  const nodes = result?.affectedNodes || [];

  const kpis = useMemo(() => {
    // --- UPDATE (elevation): rainfall run-off
    if (isRunoffScenario(scenario)) {
      return [
        { label: 'Collectors', value: (s.collectorsEvaluated ?? 0).toLocaleString(), sub: `${s.wells ?? 0} wells · ${s.catchBasins ?? 0} basins · ${s.drains ?? 0} drains` },
        { label: 'Over capacity', value: (s.overCapacity ?? 0).toLocaleString(), color: s.overCapacity ? 'orange.400' : undefined, icon: FaExclamationTriangle, sub: `${s.siltLimited ?? 0} silt-limited · ${s.blocked ?? 0} blocked` },
        { label: 'Ponding', value: (s.assetsFlooded ?? 0).toLocaleString(), color: s.assetsFlooded ? 'blue.400' : undefined, icon: FaWater, sub: s.earliestFloodHours != null ? `first ${fmtHour(s.earliestFloodHours)} · max ${s.maxFloodDepthM} m` : 'none forecast' },
        { label: 'Run-off', value: `${(s.totalRunoffM3 ?? 0).toLocaleString()} m³`, sub: `C = ${s.runoffCoefficient ?? '—'}`, icon: FaCloudRain },
        { label: 'Overflow downhill', value: `${(s.totalOverflowM3 ?? 0).toLocaleString()} m³`, sub: `${s.downhillRoutes ?? 0} routes`, icon: FaArrowDown },
        { label: 'Terrain (TessaDEM)', value: `${s.terrainCoveragePct ?? 0}%`, color: (s.terrainCoveragePct ?? 0) < 50 ? 'orange.400' : 'green.400', sub: 'collectors with terrain' },
      ];
    }
    // --- END UPDATE
    if (storm) {
      return [
        { label: 'Assets evaluated', value: (s.evaluatedAssets ?? 0).toLocaleString(), sub: `${s.cascadeOnlyAssets ?? 0} via cascade` },
        { label: 'Moderate+ damage', value: (s.moderateOrWorse ?? 0).toLocaleString(), color: s.moderateOrWorse ? 'red.400' : undefined, icon: FaExclamationTriangle },
        { label: 'Assets flooded', value: (s.assetsFlooded ?? 0).toLocaleString(), sub: s.earliestFloodHours != null ? `first ${fmtHour(s.earliestFloodHours)}` : 'none forecast', color: s.assetsFlooded ? 'blue.400' : undefined, icon: FaWater },
        { label: 'Roads impassable', value: `${s.roadsImpassable ?? 0}/${s.roadsEvaluated ?? 0}`, icon: FaRoute },
        { label: 'Expected repairs', value: fmtUsd(s.expectedRepairCostUsd), sub: `of ${fmtUsd(s.exposedReplacementValueUsd)} exposed`, icon: FaDollarSign },
        { label: 'Peak wind', value: `${s.peakWindKts ?? 0} kt`, sub: `max flood ${s.maxFloodDepthM ?? 0} m`, icon: FaWind },
      ];
    }
    if (scenario === 'climate_flooding') {
      return [
        { label: 'Inundated', value: s.directlyInundated ?? 0, color: 'blue.400', icon: FaWater },
        { label: 'Affected (cascade)', value: s.totalAffected ?? 0 },
        { label: 'Urgent hardening', value: s.urgentHardening ?? 0, color: s.urgentHardening ? 'red.400' : undefined },
        { label: 'Sinkhole risk', value: s.peakSinkholeRisk ?? '—' },
      ];
    }
    return [
      { label: 'Affected assets', value: s.totalAffected ?? 0 },
      { label: 'Critical', value: s.criticalCount ?? 0, color: s.criticalCount ? 'red.400' : undefined, icon: FaExclamationTriangle },
      { label: 'Cascade depth', value: s.maxDepth ?? 0, sub: Object.entries(s.byLayer || {}).map(([k, v]) => `${k} ${v}`).join(' · ') },
      scenario === 'pipe_burst' ? { label: 'Sinkhole risk', value: s.peakSinkholeRisk ?? '—' }
        : scenario === 'contamination' ? { label: 'Advisory zones', value: s.advisoryZones ?? 0, sub: `${s.immediateIsolationPoints ?? 0} immediate isolations` }
        : scenario === 'power_outage' ? { label: 'Boil-water zones', value: s.boilWaterAdvisoryZones ?? 0 }
        : { label: 'Layers hit', value: Object.keys(s.byLayer || {}).length },
    ];
  }, [storm, scenario, s]);

  const exportJson = useCallback(() => {
    download(JSON.stringify({ meta, result }, null, 2), `simulation-${scenario}-${Date.now()}.json`, 'application/json');
  }, [meta, result, scenario]);

  const exportCsv = useCallback(() => {
    const cols = ['asset_id', 'name', 'asset_type', 'layer', 'state', 'impact_score', 'damage_state', 'peak_wind_kts',
      'peak_flood_depth_m', 'time_to_flood_hours', 'hours_flooded', 'expected_repair_cost_usd', 'restoration_days',
      'restored_by_day', 'hardening_priority', 'cascade_from_asset_id', 'simulated_psi', 'latitude', 'longitude'];
    const lines = nodes.map(r => cols.map(c => csvCell(r[c])).join(','));
    download([cols.join(','), ...lines].join('\n'), `simulation-${scenario}-${Date.now()}.csv`, 'text/csv;charset=utf-8');
  }, [nodes, scenario]);

  if (!result) return null;

  const warnings = result.warnings || [];
  const storm_ = result.parameters?.storm;

  return (
    <VStack align="stretch" spacing={3}>
      <Box bg={cardBg} borderWidth="1px" borderColor={border} borderRadius="xl" p={3}>
        <Flex justify="space-between" align="flex-start" gap={2}>
          <Box minW={0}>
            <HStack spacing={1.5}>
              <Text fontWeight="900" fontSize="sm" noOfLines={1}>{meta?.title || scenario}</Text>
              {storm && <Badge colorScheme="purple" fontSize="9px"><Icon as={FaBrain} mr={1} boxSize={2} />AI</Badge>}
              {result.mode && <Badge fontSize="9px" variant="outline">{result.mode}</Badge>}
            </HStack>
            <Text fontSize="10px" color={muted}>
              {meta?.created_at ? new Date(meta.created_at).toLocaleTimeString() : ''}
              {meta?.duration_ms != null ? ` · ${(meta.duration_ms / 1000).toFixed(1)}s` : ''}
              {storm_ ? ` · ${CATEGORY_LABELS[storm_.category] || storm_.category} ${storm_.wind_kts} kt, ${storm_.heading_deg}° @ ${storm_.forward_speed_kts} kt` : ''}
              {result.originAssetId != null ? ` · origin #${result.originAssetId}` : ''}
            </Text>
          </Box>
          <ButtonGroup size="xs" variant="ghost" spacing={0}>
            <Tooltip label="Export JSON" hasArrow><IconButton aria-label="Export JSON" icon={<Icon as={FaFileExport} />} onClick={exportJson} /></Tooltip>
            <Tooltip label="Export CSV (per asset)" hasArrow><Button onClick={exportCsv} fontSize="9px">CSV</Button></Tooltip>
            {onClear && <Button onClick={onClear} fontSize="9px" colorScheme="red">Clear</Button>}
          </ButtonGroup>
        </Flex>

        <SimpleGrid columns={kpis.length > 4 ? 3 : 2} spacing={2} mt={3}>
          {kpis.map(k => <Kpi key={k.label} {...k} />)}
        </SimpleGrid>

        {storm && s.damageStates && <Box mt={3}><DamageBar states={s.damageStates} /></Box>}
      </Box>

      {warnings.length > 0 && (
        <Box>
          <Button size="xs" variant="ghost" colorScheme="orange" leftIcon={<Icon as={FaExclamationTriangle} />} onClick={() => setShowWarnings(v => !v)}>
            {warnings.length} model note{warnings.length > 1 ? 's' : ''}
          </Button>
          <Collapse in={showWarnings} animateOpacity>
            <VStack align="stretch" spacing={1} mt={1}>
              {warnings.map((w) => (
                <Alert key={w} status="warning" variant="left-accent" py={1.5} borderRadius="md">
                  <AlertIcon boxSize={3} />
                  <AlertDescription fontSize="10px">{w}</AlertDescription>
                </Alert>
              ))}
            </VStack>
          </Collapse>
        </Box>
      )}

      {sim.hourRange && (
        <Box bg={cardBg} borderWidth="1px" borderColor={border} borderRadius="xl" p={3}><Playback sim={sim} /></Box>
      )}

      {restoration && restoration.damaged_assets > 0 && (
        <Box bg={cardBg} borderWidth="1px" borderColor={border} borderRadius="xl" p={3}>
          <Flex justify="space-between" align="center" mb={1}>
            <HStack spacing={1.5}><Icon as={FaUsers} boxSize={3} color="blue.400" /><Text fontSize="10px" fontWeight="800" textTransform="uppercase">Restoration plan</Text></HStack>
            <Text fontSize="9px" color={muted}>{restoration.crews} crews · {restoration.total_crew_days ?? '—'} crew-days</Text>
          </Flex>
          <RestorationCurve curve={restoration.curve} />
          <SimpleGrid columns={4} spacing={1} mt={1}>
            <Kpi label="50%" value={`${restoration.days_to_50pct}d`} />
            <Kpi label="90%" value={`${restoration.days_to_90pct}d`} />
            <Kpi label="Full" value={`${restoration.days_to_full}d`} />
            <Kpi label={`Crews ≤${restoration.target_days}d`} value={restoration.crews_required_for_target ?? 'n/a'}
              color={restoration.crews_required_for_target > restoration.crews ? 'orange.400' : 'green.400'} />
          </SimpleGrid>
        </Box>
      )}

      {ensemble && (
        <Box bg={cardBg} borderWidth="1px" borderColor={border} borderRadius="xl" p={3}>
          <HStack spacing={1.5} mb={2}><Icon as={FaBrain} boxSize={3} color="purple.400" /><Text fontSize="10px" fontWeight="800" textTransform="uppercase">Ensemble · {ensemble.members} storms</Text></HStack>
          <SimpleGrid columns={3} spacing={2}>
            <Kpi label="Damaged P10" value={ensemble.assets_moderate_or_worse.p10} />
            <Kpi label="Damaged P50" value={ensemble.assets_moderate_or_worse.p50} />
            <Kpi label="Damaged P90" value={ensemble.assets_moderate_or_worse.p90} color="red.400" />
            <Kpi label="Cost P10" value={fmtUsd(ensemble.repair_cost_usd.p10)} />
            <Kpi label="Cost P50" value={fmtUsd(ensemble.repair_cost_usd.p50)} />
            <Kpi label="Cost P90" value={fmtUsd(ensemble.repair_cost_usd.p90)} color="red.400" />
          </SimpleGrid>
        </Box>
      )}

      {/* --- UPDATE (elevation): rainfall used + biggest downhill overflows */}
      {result.rain && (
        <HStack bg={cardBg} borderWidth="1px" borderColor={border} borderRadius="xl" p={2.5} spacing={3} fontSize="10px" flexWrap="wrap">
          <Badge colorScheme={result.rain.mode === 'live' ? 'cyan' : 'purple'} fontSize="9px">{result.rain.mode === 'live' ? 'LIVE RAIN' : 'DESIGN STORM'}</Badge>
          <Text>{result.rain.total_mm} mm over {result.rain.hours} h</Text>
          <Text>peak {result.rain.peak_mm_hr} mm/h</Text>
          <Text color={muted}>{result.rain.source}</Text>
        </HStack>
      )}
      {Array.isArray(result.flows) && result.flows.length > 0 && (
        <Box bg={cardBg} borderWidth="1px" borderColor={border} borderRadius="xl" p={2}>
          <Text fontSize="10px" fontWeight="800" textTransform="uppercase" px={1} mb={1}>Downhill overflow</Text>
          {result.flows.slice(0, 5).map((f) => {
            const from = nodes.find((n) => String(n.asset_id) === String(f.from_asset_id));
            const to = nodes.find((n) => String(n.asset_id) === String(f.to_asset_id));
            return (
              <Flex key={`${f.from_asset_id}-${f.to_asset_id}`} fontSize="10px" px={1} py={0.5} gap={1} align="center">
                <Text noOfLines={1} flex={1} as="button" textAlign="left" onClick={() => from && onFocusAsset?.(from)}>{from?.name || f.from_asset_id}</Text>
                <Icon as={FaArrowDown} boxSize={2} color="blue.400" transform="rotate(-90deg)" />
                <Text noOfLines={1} flex={1} as="button" textAlign="left" onClick={() => to && onFocusAsset?.(to)}>{to?.name || f.to_asset_id}</Text>
                <Text fontFamily="mono" color={muted} flexShrink={0}>{f.volume_m3} m³ · ↓{f.drop_m} m</Text>
              </Flex>
            );
          })}
        </Box>
      )}
      {result.method && <Text fontSize="9px" color={muted} px={1}>{result.method}</Text>}
      {/* --- END UPDATE */}

      {weather?.used && (
        <HStack bg={cardBg} borderWidth="1px" borderColor={border} borderRadius="xl" p={2.5} spacing={3} fontSize="10px" flexWrap="wrap">
          <Badge colorScheme="cyan" fontSize="9px">LIVE WEATHER</Badge>
          <Text>wind {weather.peak_wind_kts ?? '—'} kt</Text>
          <Text>gust {weather.peak_gust_kts ?? '—'} kt</Text>
          <Text>{weather.min_pressure_hpa ?? '—'} hPa</Text>
          <Text>rain {weather.mean_rain_mm ?? '—'} mm</Text>
          <Text color={muted}>{weather.grid_points} pts</Text>
        </HStack>
      )}

      <Box bg={cardBg} borderWidth="1px" borderColor={border} borderRadius="xl" p={2}>
        <Flex justify="space-between" px={1} mb={1}>
          <Text fontSize="10px" fontWeight="800" textTransform="uppercase">Most at risk</Text>
          <Text fontSize="9px" color={muted}>
            {nodes.length.toLocaleString()}{result.resultsTruncated ? ` of ${result.totalAffectedNodes.toLocaleString()}` : ''} assets
          </Text>
        </Flex>
        {nodes.length === 0 ? (
          <Text fontSize="11px" color={muted} px={2} py={3}>No assets affected under these conditions.</Text>
        ) : (
          <VStack align="stretch" spacing={0}>
            {nodes.slice(0, listLimit).map(r => <AtRiskRow key={r.asset_id} r={r} storm={storm} onFocus={onFocusAsset} />)}
            {nodes.length > listLimit && (
              <Button size="xs" variant="ghost" onClick={() => setListLimit(l => l + 25)}>Show more</Button>
            )}
          </VStack>
        )}
      </Box>
    </VStack>
  );
};

export default memo(SimulationResultsView);
// --- END UPDATE